/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks, PlusCircle, Search, Edit2, Trash2,
  Filter, X, Activity, ChevronLeft, ChevronRight
} from 'lucide-react';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  status: 'active', // 'active' | 'all' | 'inactive'
};

const ServiceRequestTypes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformScope = user?.scope === 'platform';

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setDebouncedFilters(filters), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchTypes = async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const includeInactive = debouncedFilters.status !== 'active';
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
        search: debouncedFilters.search || undefined,
        includeInactive: includeInactive || undefined,
      };

      const { data } = await serviceRequestTypeService.getServiceRequestTypes(params);
      if (currentRequestId !== requestIdRef.current) return;

      let rawData = data.data || [];
      let total = data.total ?? 0;

      // Handle inactive-only filter if selected
      if (debouncedFilters.status === 'inactive') {
        rawData = rawData.filter(t => !t.isActive);
        total = rawData.length;
      }

      setTypes(rawData);
      setPagination({
        page: data.page ?? currentPagination.page,
        limit: data.limit ?? currentPagination.limit,
        total: total,
        totalPages: Math.ceil(total / (data.limit ?? currentPagination.limit)),
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(err.response?.data?.message || `Failed to load ${SERVICE_REQUEST_LABEL.singularLower} types`);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchTypes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchTypes(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDeactivateConfirm = async () => {
    try {
      setDeactivating(true);
      await serviceRequestTypeService.deleteServiceRequestType(deactivateTarget.uid);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} type deactivated successfully`);
      setDeactivateTarget(null);
      fetchTypes(pagination.page);
    } catch (err) {
      toastService.error(err.response?.data?.message || `Failed to deactivate ${SERVICE_REQUEST_LABEL.singularLower} type`);
    } finally {
      setDeactivating(false);
    }
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.status !== 'active'
  );

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {SERVICE_REQUEST_LABEL.singular} Types
            </h1>
            <p className="text-xs text-base-content/50">
              Platform-wide catalog of {SERVICE_REQUEST_LABEL.singularLower} categories and custom identifier display labels.
            </p>
          </div>

          {isPlatformScope && (
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={() => navigate('/service-requests/types/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New Type</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className="relative col-span-12 sm:col-span-8 lg:col-span-9">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by category name..."
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="col-span-12 sm:col-span-4 lg:col-span-3">
            <select
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="active">Active Only</option>
              <option value="all">All Statuses (Active & Inactive)</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'active' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Activity size={10} className="text-primary" />
                  <span>
                    Status: {filters.status === 'all' ? 'All (Active & Inactive)' : 'Inactive Only'}
                  </span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, status: 'active' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : types.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <ListChecks size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? `No matching ${SERVICE_REQUEST_LABEL.singularLower} types` : `No ${SERVICE_REQUEST_LABEL.singularLower} types found`}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search query or reset status filters.'
                  : `Get started by creating your first platform ${SERVICE_REQUEST_LABEL.singularLower} type.`}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : isPlatformScope ? (
              <button
                type="button"
                onClick={() => navigate('/service-requests/types/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create {SERVICE_REQUEST_LABEL.singular} Type</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">Name</th>
                  <th className="py-3.5 px-4">{SERVICE_REQUEST_LABEL.singular} Number Label</th>
                  <th className="py-3.5 px-4">Customer Name Label</th>
                  <th className="py-3.5 px-4">Asset Identifier Label</th>
                  <th className="py-3.5 px-4">Equipment Identifier Label</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  {isPlatformScope && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {types.map((t) => (
                  <tr key={t.uid} className="hover:bg-base-200/40 transition-colors group">
                    {/* Name */}
                    <td className="py-3.5 px-5 font-bold">
                      {isPlatformScope ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/service-requests/types/${t.uid}/edit`)}
                          className="text-primary hover:underline font-bold text-sm tracking-tight text-left"
                        >
                          {t.name}
                        </button>
                      ) : (
                        <span className="font-bold text-sm text-base-content">{t.name}</span>
                      )}
                    </td>

                    {/* Request Number Label */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/70 font-medium">
                        {t.requestNumberLabel || '—'}
                      </span>
                    </td>

                    {/* Customer Name Label */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/70 font-medium">
                        {t.customerNameLabel || '—'}
                      </span>
                    </td>

                    {/* Asset Identifier Label */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/70 font-medium">
                        {t.assetIdentifierLabel || '—'}
                      </span>
                    </td>

                    {/* Equipment Identifier Label */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/70 font-medium">
                        {t.equipmentIdentifierLabel || '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 text-center">
                      <StatusBadge status={t.isActive ? 'active' : 'inactive'} />
                    </td>

                    {/* Actions */}
                    {isPlatformScope && (
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Edit">
                            <button
                              type="button"
                              onClick={() => navigate(`/service-requests/types/${t.uid}/edit`)}
                              className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                              aria-label="Edit Type"
                            >
                              <Edit2 size={15} />
                            </button>
                          </div>
                          {t.isActive && (
                            <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Deactivate">
                              <button
                                type="button"
                                onClick={() => setDeactivateTarget(t)}
                                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                aria-label="Deactivate Type"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && types.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> types
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Deactivate Confirmation Modal */}
      <Modal
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        title={`Deactivate ${SERVICE_REQUEST_LABEL.singular} Type`}
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to deactivate{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {deactivateTarget?.name}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            Deactivating this category will prevent it from being chosen when creating new {SERVICE_REQUEST_LABEL.pluralLower}. Existing {SERVICE_REQUEST_LABEL.pluralLower} referencing it will remain unaffected.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeactivateTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deactivating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeactivateConfirm}
            disabled={deactivating}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deactivating ? 'Deactivating...' : 'Deactivate Type'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceRequestTypes;
